import React, { useEffect, useRef, useState } from 'react';
import styles from './RevealImage.module.sass';

/**
 * A page asset that eases into place the first time it scrolls into view.
 *
 * Two deliberate constraints shape this component:
 *
 * 1. Every asset is positioned with `transform: translate(...)` in its own
 *    stylesheet, and those placements were tuned by hand for desktop and for
 *    mobile. So the animation never touches `transform`. It uses the separate
 *    `translate` and `rotate` properties, which the browser composes with
 *    `transform` rather than replacing it. The resting position is exactly the
 *    one the stylesheet asks for, to the pixel.
 *
 * 2. Nothing here is random. A photograph drifts in from the side of the page
 *    it occupies — outside edge toward its text — and never rotates, because a
 *    tilted portrait reads as a gimmick. Decorative shapes rise and unwind a
 *    small, constant tilt, which suits the organic blobs the brand is built on.
 */

// Motion, in one place so it stays consistent across the site.
const PHOTO_DRIFT_REM = 2.25; // horizontal, toward the asset's own edge
const PHOTO_RISE_REM = 0.75;
const SHAPE_RISE_REM = 1.5;
const SHAPE_TILT_DEG = -2; // one fixed direction; deliberately not per-asset

// How far from centre an asset must sit before it earns a horizontal entrance.
// Anything nearer the middle — which is where photos land on a phone — simply
// rises, because drifting sideways from dead centre looks arbitrary.
const EDGE_THRESHOLD = 0.08;

type RevealImageProps = {
  className?: string;
  src: string;
  alt?: string;
  /** Photographs: no rotation, and they enter from their own side of the page. */
  photo?: boolean;
};

type RevealStyle = React.CSSProperties & {
  '--reveal-x': string;
  '--reveal-y': string;
  '--reveal-rot': string;
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Which way this asset should travel, decided from where it actually sits at
 * the moment it is revealed. Reading it off the rendered box means desktop and
 * mobile each get the right answer without either layout being described twice.
 */
const entranceFor = (element: HTMLImageElement, photo: boolean): RevealStyle => {
  if (!photo) {
    return {
      '--reveal-x': '0rem',
      '--reveal-y': `${SHAPE_RISE_REM}rem`,
      '--reveal-rot': `${SHAPE_TILT_DEG}deg`,
    };
  }

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  // -0.5 at the left edge, 0 at the centre, +0.5 at the right edge.
  const offsetFromCentre = (rect.left + rect.width / 2) / viewportWidth - 0.5;

  let drift = 0;
  if (offsetFromCentre > EDGE_THRESHOLD) drift = PHOTO_DRIFT_REM;
  else if (offsetFromCentre < -EDGE_THRESHOLD) drift = -PHOTO_DRIFT_REM;

  return {
    '--reveal-x': `${drift}rem`,
    '--reveal-y': `${PHOTO_RISE_REM}rem`,
    '--reveal-rot': '0deg',
  };
};

/**
 * True when the element sits entirely outside the horizontal bounds of whatever
 * ancestor clips it, meaning no amount of scrolling can bring it into view.
 */
const isClippedOutOfView = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return true;

  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const overflowX = window.getComputedStyle(parent).overflowX;
    if (overflowX === 'visible') continue;
    const bounds = parent.getBoundingClientRect();
    if (rect.right <= bounds.left || rect.left >= bounds.right) return true;
  }
  return false;
};

export default function RevealImage({ className, src, alt = '', photo = false }: RevealImageProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [entrance, setEntrance] = useState<RevealStyle | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let cancelled = false;

    const show = () => {
      if (cancelled) return;
      // Decide the direction from the box as it stands right now, then let the
      // browser paint that starting state before flipping to the resting one.
      setEntrance(entranceFor(element, photo));
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!cancelled) setRevealed(true);
      }));
    };

    // An asset whose image has not decoded yet has no height, and a percentage
    // `transform` resolves against that height — so it would sit in the wrong
    // place and jump once the bytes arrived. Waiting for the load means any
    // such correction happens while the asset is still invisible.
    const whenLoaded = (run: () => void) => {
      if (element.complete && element.naturalWidth > 0) return run();
      const done = () => {
        element.removeEventListener('load', done);
        element.removeEventListener('error', done);
        run();
      };
      element.addEventListener('load', done);
      element.addEventListener('error', done);
    };

    if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
      setRevealed(true);
      return;
    }

    // Some decorative shapes are pushed clear of the page by their own
    // transform (translate(120%) and the like) and are then clipped away by an
    // ancestor's overflow. IntersectionObserver honours that clipping, so such
    // an asset can never report an intersection and would stay at opacity 0
    // for good — which is how one of them quietly vanished. It is not visible
    // either way, so hand it the finished state and skip the observer.
    if (isClippedOutOfView(element)) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          whenLoaded(show);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(element);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [photo]);

  return (
    <img
      ref={ref}
      className={[styles.Reveal, revealed ? styles.Revealed : '', className]
        .filter(Boolean)
        .join(' ')}
      style={entrance || undefined}
      src={src}
      alt={alt}
    />
  );
}
